import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Input from '../../../components/input/Input';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import useManageTravelCard from '../../setting/travelCard/useManageTravelCard';
import { numberFields, validateNumberInput } from '../../../utils/formValidations';
import { toast } from 'react-toastify';
import useExchangeMoney from './useExchangeMoney';
import useCompanyBalance from '../../company/balance/useCompanyBalance';
import DatePicker from "react-datepicker";
import { formatDate } from '../../../utils/dateUtils';
import { 
  Card, 
  CardContent, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  CircularProgress 
} from '@mui/material';
import { formatAmount } from '../../../utils/formatAmount';

const initialValue = {
  thaiBaht:'',
  jpYen:'',
  rate:'',
  cardName: '',
  exchangeDate: new Date()
}

const ExchangeMoney = () => {

  const columnDefs = [
    { headerName: 'Exchange Date', field: 'exchangeDate', flex: 1 },
    { headerName: 'Thai Baht', field: 'thaiBaht', flex: 1 },
    { headerName: 'Japanese Yen', field: 'jpYen', flex: 1 },
    { headerName: 'Rate', field: 'rate', flex: 1 },
    { headerName: 'Card Name', field: 'cardName', flex: 1 },
    { headerName: 'รอบเรือ', field: 'shipDate', flex: 1 },
  ];

  const [formData, setFormData] = useState(initialValue);
  const [cardNameFilter, setCardNameFilter] = useState('');
  const {travelCards, refreshTravelCards} = useManageTravelCard()
  const {moneys, saveExchangeMoney, getExchangeMoneys} = useExchangeMoney()
  const {balance, isLoading: balanceLoading, error: balanceError, getCompanyBalance} = useCompanyBalance()
  const [rowData, setRowData] = useState([])

  const uniqueCardNames = [...new Set(moneys.map(m => m.cardName).filter(Boolean))].sort();
  const filteredRowData = cardNameFilter
    ? rowData.filter(row => row.cardName === cardNameFilter)
    : rowData;

  // When user selects a card in the filter, limit the "input" Travel Card dropdown to that card too.
  // This matches the behavior you want while keeping the grid filter as-is.
  useEffect(() => {
    if (!cardNameFilter) return;
    setFormData((prev) => ({ ...prev, cardName: cardNameFilter }));
  }, [cardNameFilter]);

  const handleChange = (e) => {
    setFormData(formData => ({
      ...formData,
      cardName: e.target.value
    }))
  };

  const handleDateChange = (name, selectedDate) => {
    setFormData(prevOrder => ({
        ...prevOrder,
        [name]: selectedDate
    }));
	};

  const handleInputChange = (name, value) => {
    if (numberFields.has(name)) {
			value = validateNumberInput(value, formData[name]);
		}
    setFormData(formData => ({
      ...formData,
      [name]:value
    }))
  }

  useEffect(() => { 
    const formattedMoneys = moneys.map(money => ({
    ...money, // spread the other properties of the order
    exchangeDate: formatDate(money.exchangeDate), // overwrite orderDate with the formatted version
    shipDate: money.ShipDate ? formatDate(money.ShipDate.shipDate) : ""
  }));
  console.log(formattedMoneys)
    setRowData(formattedMoneys)
  }, [moneys]);
  
  const handleSaveButton = async (e) => {
    e.preventDefault()
    if(formData.rate == ''){
      toast.error("Please input both Thai Baht and Jp Yen")
    }else if(formData.rate > 0.35 || formData.rate < 0.15){
      toast.error("Rate out of range. Maybe input wrong number. ")
    }else if(formData.cardName == ''){
      toast.error("Please select Travel Card")
    }else{
      console.log(formData)
      const response = await saveExchangeMoney(formData)
      if(response === true){
        toast.success("Add Exchange Data Successfully!!")
        // Keep the filtered card selected so the next entry doesn't need re-selecting
        setFormData({ ...initialValue, cardName: cardNameFilter })
        getCompanyBalance()
      }else{
        toast.error(response)
      }
    }
  }

  useEffect(() => {
    const { thaiBaht, jpYen } = formData;
    if (thaiBaht && jpYen) {
      const rate = (parseFloat(thaiBaht) / parseFloat(jpYen)).toFixed(4); // you can adjust the precision as needed
      setFormData((prevFormData) => ({
        ...prevFormData,
        rate, // updating the rate in your formData state
      }));
    }
  }, [formData.thaiBaht, formData.jpYen]);

  useEffect(() => {
		refreshTravelCards()
    getExchangeMoneys()
	},[])

  return (
    <div className="ag-theme-alpine" style={{ width: '100%' }}>
      <h2>
        Exchange Money
      </h2>

      <div className='flex items-center gap-4 mb-4'>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="cardName-filter-label">Filter by Card</InputLabel>
          <Select
            labelId="cardName-filter-label"
            value={cardNameFilter}
            onChange={(e) => setCardNameFilter(e.target.value)}
            label="Filter by Card"
          >
            <MenuItem value="">All cards</MenuItem>
            {uniqueCardNames.map((cardName) => (
              <MenuItem key={cardName} value={cardName}>{cardName}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      
      <div className='grid grid-cols-3'>
        <div className='col-span-2'>
        <AgGridReact
          columnDefs={columnDefs}
          rowData={filteredRowData}
          domLayout='autoHeight'
          defaultColDef={{ resizable: true }}
        />
        </div>
        <div className='col-span-1'>
          <form className='mx-5 border-gray-300 border rounded-lg mb-4'>
            <div className='bg-gray-200 px-4 py-2 rounded-t-lg border-t'>
              <h3 className='text-3xl '>Input New Data</h3>
            </div>
            <div className='p-5 mx-1'>
            <InputLabel 
                id="cardName-label" 
                style={{
                  fontSize:'1.1em',
                  color:'black',
                  paddingLeft:'2px'
                }}
              >
                Exchange Date
              </InputLabel>
              <DatePicker 
								selected={formData?.exchangeDate}
								onChange={(date) => handleDateChange("exchangeDate", date)}
								dateFormat={"yyyy-MM-dd"}
								className='datepicker2 w-full mb-4'
							/>
              <Input title={'Thai Baht'} name={'thaiBaht'} value={formData?.thaiBaht} onChange={value => handleInputChange('thaiBaht',value)} /* customClass={'mb-4'} *//>
              <Input title={'Japanese Yen'} name={'jpYen'} value={formData?.jpYen} onChange={value => handleInputChange('jpYen',value)} /* customClass={'mb-4'} *//>
              <Input title={'Exhchange Rate'} name={'rate'} value={formData?.rate} disable={true} /* customClass={'mb-4'} *//>
              <InputLabel 
                id="cardName-label" 
                style={{
                  fontSize:'1.1em',
                  color:'black',
                  paddingLeft:'2px'
                }}
              >
                Travel Card
              </InputLabel>
              <FormControl fullWidth size="small">
                <Select
                  labelId="cardName-label"
                  id="cardName"
                  value={formData?.cardName}
                  onChange={handleChange}
                >
                  {travelCards &&
                    travelCards
                      .filter((card) =>
                        cardNameFilter ? card.cardName === cardNameFilter : true
                      )
                      .map((card) => (
                        <MenuItem key={card.id} value={card.cardName}>
                          {card.cardName}
                        </MenuItem>
                      ))}
                    
                </Select>
              </FormControl>
              <button className='w-32 btn btn-save text-lg mt-3' onClick={handleSaveButton}>Save</button>
            </div>
          </form>

          {/* Company Balance Card */}
          <Card className='mx-5 mb-4'>
            <CardContent>
              <Typography variant="h6" component="h3" gutterBottom>
                Company Balance
              </Typography>
              
              {balanceLoading ? (
                <div className="flex justify-center items-center py-4">
                  <CircularProgress size={24} />
                </div>
              ) : balanceError ? (
                <Typography color="error" variant="body2">
                  {balanceError}
                </Typography>
              ) : balance ? (
                <div>
                  {/* Summary Data */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <Typography variant="body2" color="textSecondary">
                        Total Amount
                      </Typography>
                      <Typography variant="h6" color="primary">
                        ¥{formatAmount(balance.totalAmount)}
                      </Typography>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <Typography variant="body2" color="textSecondary">
                        Average Rate
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {parseFloat(balance.averageRate).toFixed(4)}
                      </Typography>
                    </div>
                  </div>

                  {/* Queue Table */}
                  {balance.queue && balance.queue.length > 0 && (
                    <div>
                      <Typography variant="subtitle2" gutterBottom>
                        Exchange Queue
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Amount (¥)</TableCell>
                              <TableCell>Rate</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {balance.queue.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>{formatAmount(item.amount)}</TableCell>
                                <TableCell>{item.rate.toFixed(4)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  )}
                </div>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No balance data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default ExchangeMoney